<template>
  <modal-fullscreen
    class="profile-modal"
    :visible="visible"
    :destroy-on-close="true"
    width="1300px"
    wrap-class-name="profile-view-modal"
    data-testid="profile-view-modal"
    @cancel="closeModal"
  >
    <rate-modal :visible.sync="visibleRateModal" data-testid="profile-view-rate-modal" />
    <legal-entity-modal
      :editing-agency="viewedLegalEntity"
      :visible.sync="visibleLegalEntity"
      data-testid="profile-view-legal-entity-modal"
      @add="reloadAssociate(true)"
    />

    <template slot="title">
      <div v-if="associate" class="profile-modal-title" data-testid="profile-view-title">
        {{ associate.first_name }}
        {{ associate.last_name }} (ID: {{ associate.associate_number }})

        <a-tooltip>
          <template slot="title"> Copy Profile Link </template>
          <span @click="copyLink" class="action-button" data-testid="profile-view-copy-link-button"
            ><custom-icon width="20" height="20" icon="carbon:link"
          /></span>
        </a-tooltip>

        <a-tooltip>
          <template slot="title">
            {{ associate.is_verified ? 'Complete' : 'Incomplete' }}
          </template>
          <custom-icon
            :style="{ color: associate.is_verified ? '#10b981' : '#ef4444' }"
            width="28px"
            height="28px"
            :icon="associate.is_verified ? 'carbon:checkmark-filled' : 'carbon:close-filled'"
          />
        </a-tooltip>

        <a-tooltip>
          <template slot="title">
            {{ associate.epeople_certified ? 'Certified' : 'Uncertified' }}
          </template>
          <custom-icon
            width="28px"
            height="28px"
            icon="carbon:certificate"
            :style="{
              color: associate.epeople_certified ? '#10b981' : '#ef4444'
            }"
          />
        </a-tooltip>
      </div>
      <div v-else>Associate Profile</div>
    </template>

    <div v-if="!associate || loading" class="loading-spin" style="height: 650px" data-testid="profile-view-loading">
      <a-spin />
    </div>

    <div v-else :key="associate_number" data-testid="profile-view-content">
      <clone-associate
        :visible.sync="visibleCloneModal"
        :associate-number="associate.associate_number"
        data-testid="profile-view-clone-modal"
        @close="endCloneAssociate"
      />

      <profile-edit
        v-if="associate"
        :visible.sync="visibleEditModal"
        @clone="visibleCloneModal = true"
        @edit="endEditAssociate"
        @saved="handleEditSaved"
        @externalLink="linkAssociate"
        data-testid="profile-view-edit-modal"
      />
      <a-tabs v-model="activeKey" size="small" tabPosition="left" data-testid="profile-view-tabs">
        <a-tab-pane key="kpi" v-if="hasFeature('point_system')">
          <span slot="tab">
            <a-icon type="crown" />
            Rating
          </span>
          <profile-rating />
        </a-tab-pane>

        <a-tab-pane key="personal">
          <span slot="tab">
            <a-icon type="user" />
            Personal
          </span>
          <a-spin :spinning="loading">
            <profile-personal
              ref="personal"
              @open="visibleLegalEntity = true"
              @clone="visibleCloneModal = true"
              @edit="handlePersonalEdit"
              @openAssignment="openStuffProfile"
              @updateRate="visibleRateModal = true"
            />
          </a-spin>
        </a-tab-pane>
        <a-tab-pane key="job">
          <span slot="tab">
            <a-icon type="bank" />
            Job
          </span>
          <a-spin :spinning="loading">
            <profile-job @open="visibleLegalEntity = true" @externalLink="linkAssociate" />
          </a-spin>
        </a-tab-pane>
        <a-tab-pane key="disciplinary">
          <span slot="tab">
            <a-icon type="unordered-list" />
            Disciplinary
          </span>
          <profile-disciplinary />
        </a-tab-pane>
        <a-tab-pane v-if="hasFeature('document')" key="associate_documents">
          <span slot="tab">
            <a-icon type="file-done" />
            Documents
          </span>
          <a-spin :spinning="loadingSections.sync_documents">
            <profile-documents
              :compliance-loading="
                loadingSections.compliance || loadingSections.certification || loadingSections.performance_review
              "
              @open="visibleLegalEntity = true"
            />
          </a-spin>
        </a-tab-pane>
        <a-tab-pane v-if="hasFeature('document')" key="associate_trainings">
          <span slot="tab">
            <a-icon type="read" />
            Trainings
          </span>
          <profile-trainings />
        </a-tab-pane>
        <a-tab-pane key="shifts">
          <span slot="tab">
            <a-icon type="unordered-list" />
            Shifts
          </span>
          <profile-shifts />
        </a-tab-pane>
        <a-tab-pane key="time-off">
          <span slot="tab">
            <a-icon type="calendar" />
            Time Off
          </span>
          <profile-time-off />
        </a-tab-pane>
        <a-tab-pane key="history">
          <span slot="tab">
            <a-icon type="database" />
            Changes History
          </span>
          <a-spin :spinning="loadingSections.changes_history">
            <profile-history />
          </a-spin>
        </a-tab-pane>
      </a-tabs>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between" data-testid="profile-view-footer">
        <a-button data-testid="profile-view-close-button" @click="closeModal">Close</a-button>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import { Button, Icon, message, Modal, notification, Spin, Tabs, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ProfileViewPersonal from './profile-view-personal.vue'
import ProfileViewJob from './job/job.vue'
import ProfileAssociateClone from './profile-associate-clone.vue'
import ProfileAssociateEdit from './profile-associate-edit.vue'
import ProfileViewDocuments from './profile-view-documents.vue'
import ProfileViewTrainings from './profile-view-trainings.vue'
import profileViewChangesHistory from './profile-view-changes-history.vue'
import featureMixin from '@/mixins/featureMixin'
import LegalEntityModal from '@/modules/legal-entity/components/legal-entity-modal.vue'
import AppIcon from '@/components/ui/app-icon.vue'
import ProfileViewShift from './profile-view-shift.vue'
import ProfileViewTimeOff from './profile-view-time-off.vue'
import ProfileViewDisciplinary from './profile-view-disciplinary.vue'
import RatingHistory from './rating/rating-history.vue'
import permissionMixin from '@/mixins/permissionMixin'
import RateModal from './rate/rate-modal.vue'
import { buildProjectDeepLinkQuery } from '@/utils/deep-link'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: documentActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-tabs': Tabs,
    'a-icon': Icon,
    'a-tab-pane': Tabs.TabPane,
    'a-button': Button,
    'a-spin': Spin,
    'a-tooltip': Tooltip,
    'custom-icon': AppIcon,
    'rate-modal': RateModal,
    'profile-personal': ProfileViewPersonal,
    'profile-documents': ProfileViewDocuments,
    'profile-trainings': ProfileViewTrainings,
    'profile-job': ProfileViewJob,
    'profile-history': profileViewChangesHistory,
    'profile-shifts': ProfileViewShift,
    'profile-time-off': ProfileViewTimeOff,
    'profile-disciplinary': ProfileViewDisciplinary,
    'profile-rating': RatingHistory,
    'clone-associate': ProfileAssociateClone,
    'profile-edit': ProfileAssociateEdit,
    'legal-entity-modal': LegalEntityModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate_number: {
      type: String,
      default: undefined
    },
    project_id: {
      type: Number,
      default: undefined
    },
    edit: {
      type: Function,
      default: () => {}
    },
    openTab: {
      type: String,
      default: 'personal'
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      loadingSections: {
        sync_documents: false,
        compliance: false,
        certification: false,
        performance_review: false,
        changes_history: false
      },

      visibleCloneModal: false,
      visibleEditModal: false,
      visibleLegalEntity: false,
      visibleRateModal: false,

      activeKey: 'personal',

      viewedLegalEntity: null,

      reopenEdit: false
    }
  },
  mixins: [featureMixin, permissionMixin],
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.activeKey = this.normalizeOpenTab(this.openTab)
        }
      },
      immediate: true
    },
    visibleEditModal(newVal) {
      if (!newVal) {
        this.$refs.personal?.clearPendingSequenceAction?.()
      }
    },
    associate_number: {
      async handler(newVal) {
        if (newVal) {
          await this.fetchAssociate()
        }
      },
      immediate: true
    },
    refetchAssociate: {
      handler(newVal) {
        if (newVal) {
          this.fetchAssociate()
          this.$emit('edit', this.associate)
        }
      }
    },
    associate: {
      handler(newVal) {
        this.viewedLegalEntity = newVal?.payment_info?.legal_entity

        if (newVal) this.$emit('edit', newVal)
      },
      deep: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      refetchAssociate: state => state.refetchAssociate,
      compliance: state => state.associateCompliance
    }),

    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    }
  },
  methods: {
    ...documentActions(['getSyncAssociateDocuments', 'getDynamicForm', 'getAvailabilityDynamicForm']),
    ...profileActions([
      'getAssociate',
      'getCreationContext',
      'reloadAssociate',
      'getAssociateCompliance',
      'getAssociateCertification',
      'postLinkToStuffAssociate',
      'postCreateStuffAssociate',
      'setAssociate',
      'setAssociateCompanyFlow'
    ]),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    normalizeOpenTab(tab) {
      const aliases = {
        associate_lifecycle: 'associate_documents',
        schedule: 'job',
        'critical-task': 'job',
        permissions: 'personal',
        notes: 'personal',
        facilities: 'job'
      }

      return aliases[tab] || tab || 'personal'
    },

    endCloneAssociate(newAssociateNumber) {
      this.$emit('update:associate_number', newAssociateNumber)
    },

    handlePersonalEdit() {
      this.visibleEditModal = true
    },

    linkAssociate(type) {
      this.linkStuff(type)
    },

    async linkStuff(type) {
      Modal.confirm({
        title: type === 'link_to_staff_associate' ? 'Connect Profiles?' : 'Disconnect Profiles?',
        content:
          type === 'link_to_staff_associate'
            ? "By connecting, the associate's shifts will be SYNCHRONIZED. The Staff Company will be able to pay them through their own payroll. However, some profile changes will be partially unavailable. Are you sure you want to connect the profiles?"
            : 'By disconnecting, shift SYNCHRONIZATION will STOP. The associate will no longer be able to receive payment for shifts through the Staff Company. However, all restrictions on profile changes will be removed. Are you sure you want to disconnect the profiles?',
        onOk: async () => {
          try {
            const data = await this.postLinkToStuffAssociate({
              id: this.associate.id,
              type
            })

            if (type === 'link_to_staff_associate' && !data.can_link) {
              await this.createStuff()
            }

            this.reloadAssociate(true)
          } catch (error) {
            this.showNotification('error', 'Error', error.message || error)
          }
        },
        onCancel() {}
      })
    },

    async createStuff() {
      if (this.$store.state.applicationState.currentProject.id !== 1) {
        this.showNotification(
          'info',
          'Info',
          "We couldn't Connect the Profiles! The person doesn't work in the Staff Company and doesn't have a profile there. Please contact the Staff Company manager to resolve the issue!"
        )
        return
      }

      await new Promise(resolve => {
        Modal.confirm({
          title: 'Create a New Profile?',
          content:
            "We couldn't find a profile in the Staff Company to establish the connection. Please clarify the employment details for this person. You can copy the profile from your company into the Staffing Company by creating a new profile. Would you like to do that?",
          okText: 'Create New Profile',
          onOk: async () => {
            try {
              await this.postCreateStuffAssociate({
                id: this.associate.id
              })

              resolve()
            } catch (error) {
              this.showNotification('error', 'Error', 'Unable to Create a New Profile')
              resolve()
            }
          },
          onCancel: () => {
            resolve()
          }
        })
      })
    },

    async openStuffProfile(associate_number) {
      this.loading = true

      this.activeKey = 'personal'

      try {
        await this.$router
          .replace({
            name: 'home',
            query: {}
          })
          .catch(() => {})

        await this.$store.dispatch('loadApplicationState')

        if (this.associate_number === associate_number) {
          await this.fetchAssociate()
        } else {
          this.endEditAssociate(associate_number)
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.closeModal()
      }
    },

    endEditAssociate(newAssociateNumber) {
      this.$emit('update:associate_number', newAssociateNumber)
      this.$emit('edit', this.associate)
    },

    handleEditSaved() {
      this.$refs.personal?.continuePendingSequenceAction?.()
    },

    closeModal() {
      this.$emit('update:visible', false)
      this.setAssociate(undefined)
      this.setAssociateCompanyFlow(undefined)
      this.$emit('update:associate_number', undefined)
      this.$emit('update:project_id', undefined)
    },

    copyLink() {
      const mergedQuery = buildProjectDeepLinkQuery({
        currentQuery: this.$route.query,
        deepLinkQuery: {
          editingAssociate: this.associate.associate_number,
          tab: this.activeKey
        },
        projectId: this.$store.state.applicationState.currentProject?.id
      })

      const resolved = this.$router.resolve({
        name: this.$route.name,
        query: mergedQuery
      })

      const fullUrl = window.location.origin + resolved.href

      navigator.clipboard
        .writeText(fullUrl)
        .then(() => {
          message.success('Link to profile successfully copied!', 5)
        })
        .catch(() => {
          message.error('Unable copy link!', 5)
        })
    },

    async fetchAssociate() {
      this.loading = true

      const payload = {
        associate_number: this.associate_number,
        project_id: this.project_id
      }

      if (!this.associate_number) {
        return
      }

      try {
        await this.getAssociate(payload)

        this.visibleEditModal = this.isEditing
        this.loading = false

        await Promise.all([
          (async () => {
            this.loadingSections.compliance = true
            try {
              await this.getAssociateCompliance({
                associate_id: this.associate.id
              })
            } catch (error) {
              this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
            } finally {
              this.loadingSections.compliance = false
            }
          })(),
          !this.isStaff &&
            (async () => {
              this.loadingSections.performance_review = true
              try {
                await Promise.all([
                  this.getDynamicForm({
                    associate_id: this.associate.id,
                    type: 'performance_review'
                  }),
                  this.getDynamicForm({
                    associate_id: this.associate.id,
                    type: 'skills_check'
                  }),
                  this.getDynamicForm({
                    associate_id: this.associate.id,
                    type: 'annual_skills_check'
                  }),
                  this.getAvailabilityDynamicForm({
                    associate_id: this.associate.id
                  })
                ])
              } catch (error) {
                this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
              } finally {
                this.loadingSections.performance_review = false
              }
            })(),
          (async () => {
            this.loadingSections.certification = true
            try {
              await this.getAssociateCertification({
                associate_id: this.associate.id
              })
            } catch (error) {
              this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
            } finally {
              this.loadingSections.certification = false
            }
          })(),
          (async () => {
            try {
              this.loadingSections.sync_documents = true
              await this.getSyncAssociateDocuments({
                associate_id: this.associate.id
              })
            } catch (error) {
              this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
            } finally {
              this.loadingSections.sync_documents = false
            }
          })()
        ])
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.closeModal()
      } finally {
        this.loading = false
        this.reopenEdit = false
        this.reloadAssociate(false)
      }
    }
  }
}
</script>

<style scoped>
.profile-modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-modal ::v-deep .ant-modal-body {
  padding-left: 0;
}

.profile-modal ::v-deep .ant-tabs-tab {
  text-align: left !important;
}

.profile-modal ::v-deep .ant-tabs-content {
  height: 700px;
  overflow-y: auto;
  padding: 0 24px;
}

@media (max-width: 1024px) {
  .profile-modal ::v-deep .ant-tabs-content {
    height: 100%;
    overflow-y: auto;
    padding: 0 24px;
  }
}
</style>
